/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  call,
  cancelled,
  delay,
  put,
  select,
  take,
  fork,
  cancel,
} from 'redux-saga/effects'
import { actions, Actions, TConnectObject } from './actions'
import { createSagas } from 'src/helpers/redux'
import * as schemaService from 'src/services/storage/project/scheme'
import { IResponse } from 'src/services/accounts/http'
import { IListItem } from '../storage/objects/state'
import { AxiosError } from 'axios'
import { notify } from 'src/components/ui/notify/Notify'
import { selectors as accountSelectors } from 'src/store/modules/account/selectors'
import { setters } from './index'
import {
  selectBackendCall,
  selectCallSid,
  selectSubscriberCallHistory,
} from './selectors'
import type { ICall } from '../communication/types'
import type { IBackendCall, ICallParticipant, TTransferMode } from './state'

import * as twilioService from 'src/services/communication/twilio'
import type { TwilioCallEvent } from 'src/services/communication/twilio'

import { buffers, eventChannel, EventChannel, Task } from 'redux-saga'

import { Call } from '@twilio/voice-sdk'

type TwilioChannelEvent = { type: string; error?: unknown; call?: unknown }

const createTwilioChannel = () => {
  return eventChannel<TwilioChannelEvent>((emit) => {
    const unsubscribeRegistered = twilioService.twilioDeviceManager.on(
      'registered',
      () => {
        emit({ type: 'registered' })
      }
    )

    const unsubscribeUnregistered = twilioService.twilioDeviceManager.on(
      'unregistered',
      () => {
        emit({ type: 'unregistered' })
      }
    )

    const unsubscribeRegistering = twilioService.twilioDeviceManager.on(
      'registering',
      () => {
        emit({ type: 'registering' })
      }
    )

    const unsubscribeError = twilioService.twilioDeviceManager.on(
      'error',
      (error, call) => {
        emit({
          type: 'error',
          error,
          call,
        })
      }
    )

    const unsubscribeTokenWillExpire = twilioService.twilioDeviceManager.on(
      'tokenWillExpire',
      () => {
        emit({ type: 'tokenWillExpire' })
      }
    )

    const unsubscribeIncoming = twilioService.twilioDeviceManager.on(
      'incoming',
      (call) => {
        emit({ type: 'incoming', call })
      }
    )

    const unsubscribeCallChanged = twilioService.twilioDeviceManager.on(
      'callChanged',
      (call) => {
        emit({ type: 'callChanged', call })
      }
    )

    // cleanup
    return () => {
      unsubscribeIncoming()
      unsubscribeCallChanged()
      unsubscribeRegistered()
      unsubscribeUnregistered()
      unsubscribeRegistering()
      unsubscribeError()
      unsubscribeTokenWillExpire()
    }
  }, buffers.expanding<TwilioChannelEvent>())
}

// Events after which the call is over and its channel can be closed
const CALL_FINAL_EVENTS = ['disconnect', 'cancel', 'reject', 'error']

// Events of one call. Only the events of the given call get into the channel.
const createCallChannel = (call: Call) => {
  return eventChannel<TwilioCallEvent>((emit) => {
    return twilioService.twilioDeviceManager.on('callEvent', (event) => {
      if (event.call === call) emit(event)
    })
  }, buffers.expanding<TwilioCallEvent>())
}

const RESOLVE_ATTEMPTS = 3
const RESOLVE_RETRY_DELAY = 1000

const HOLD_ERROR_MESSAGES: Record<string, string> = {
  not_conference: 'This call cannot be put on hold.',
  escalation_pending: 'Hold is already in progress.',
  call_not_active: 'The call is no longer active.',
  escalation_failed: 'Could not move the call to a conference.',
  twilio_error: 'The phone provider rejected the request.',
  transfer_in_progress:
    'The client stays on hold while a transfer is in progress.',
}

const ADD_PARTICIPANT_ERROR_MESSAGES: Record<string, string> = {
  user_is_self: 'You cannot add yourself to the call.',
  user_not_active: 'This user is not available.',
  user_already_in_call: 'This user is already in the call.',
  participants_limit: 'The call has reached its participant limit.',
  transfer_in_progress:
    'A participant cannot be added while a transfer is in progress.',
  escalation_pending: 'The call is still being moved to a conference.',
  not_conference: 'This call cannot add a participant.',
  escalation_failed: 'Could not move the call to a conference.',
  twilio_error: 'The phone provider rejected the request.',
}

const ACTIVE_PARTICIPANT_STATUSES = new Set([
  'initiated',
  'ringing',
  'connected',
])

const ACTIVE_TRANSFER_STATUSES = new Set(['ringing', 'consulting'])

const TRANSFER_ERROR_MESSAGES: Record<string, string> = {
  transfer_in_progress: 'A transfer is already in progress.',
  user_is_self: 'You cannot transfer the call to yourself.',
  user_not_active: 'This user is not available.',
  user_already_in_call: 'This user is already in the call.',
  call_not_active: 'The call is no longer active.',
  not_conference: 'This call cannot be transferred.',
  escalation_pending: 'The call is still being moved to a conference.',
  escalation_failed: 'Could not move the call to a conference.',
  twilio_error: 'The phone provider rejected the request.',
  forbidden: 'You cannot change this transfer.',
  no_transfer: 'This call has no active transfer.',
  transfer_not_consulting: 'The transfer is not ready to complete.',
}

/** Complete/cancel disagreed with the server: refresh the call once, without retrying the action */
const TRANSFER_STATE_MISMATCH = new Set([
  'no_transfer',
  'transfer_not_consulting',
])

/** Known call-control failures come back as a code on 409 / 502 */
const getApiErrorMessage = (
  err: unknown,
  messages: Record<string, string>,
  fallback: string
) => {
  const data = (
    err as AxiosError<{
      code?: unknown
      error?: unknown
      errorCode?: unknown
      message?: unknown
    }>
  ).response?.data
  const code = [data?.code, data?.error, data?.errorCode, data?.message].find(
    (value): value is string => typeof value === 'string' && value in messages
  )

  return code ? messages[code] : fallback
}

const getHoldErrorMessage = (err: unknown, fallback: string) =>
  getApiErrorMessage(err, HOLD_ERROR_MESSAGES, fallback)

const getApiErrorCode = (err: unknown) => {
  const data = (
    err as AxiosError<{
      code?: unknown
      error?: unknown
      errorCode?: unknown
      message?: unknown
    }>
  ).response?.data

  return [data?.code, data?.error, data?.errorCode, data?.message].find(
    (value): value is string => typeof value === 'string'
  )
}

const getTransferErrorMessage = (err: unknown, fallback: string) => {
  if ((err as AxiosError).response?.status === 422) {
    return 'The transfer request is invalid.'
  }

  return getApiErrorMessage(err, TRANSFER_ERROR_MESSAGES, fallback)
}

const canTransferCall = (call: IBackendCall) =>
  call.mode === 'hybrid' || call.mode === 'conference'

const isUserAlreadyInCall = (call: IBackendCall, userId: number) =>
  (call.participants ?? []).some(
    (participant) =>
      participant.kind === 'user' &&
      participant.userId === userId &&
      ACTIVE_PARTICIPANT_STATUSES.has(participant.status)
  )

/** Replaces the backend call and rebuilds the presented participants */
function* applyUpdatedCall(callId: number, updated: IBackendCall) {
  const current: IBackendCall | null = yield select(selectBackendCall)
  if (current?.id !== callId) return

  yield call(commitBackendCall, updated)
}

function* refreshBackendCall(callId: number) {
  try {
    const fresh: IBackendCall = yield call(twilioService.getCall, callId)
    yield call(applyUpdatedCall, callId, fresh)
  } catch {
    // The failed action already reported its own error
  }
}

function* commitBackendCall(call: IBackendCall) {
  const currentUserId: number | undefined = yield select(
    accountSelectors.userId
  )

  yield put(
    setters.setBackendCall({ call, currentUserId: currentUserId ?? null })
  )
}

/**
 * The `accept` event can come before the backend webhook creates the call,
 * so a 404 is retried. Any other error stops the lookup.
 */
function* resolveBackendCall(callSid: string) {
  yield put(setters.setCallSid(callSid))

  for (let attempt = 1; attempt <= RESOLVE_ATTEMPTS; attempt++) {
    try {
      const backendCall: IBackendCall = yield call(
        twilioService.resolveCall,
        callSid
      )
      // The call may have already ended while the request was in flight
      const currentSid: string | null = yield select(selectCallSid)
      if (currentSid === callSid) yield call(commitBackendCall, backendCall)

      return
    } catch (err) {
      const isNotCreatedYet = (err as AxiosError).response?.status === 404
      const isLastAttempt = attempt === RESOLVE_ATTEMPTS
      if (!isNotCreatedYet || isLastAttempt) return

      yield delay(RESOLVE_RETRY_DELAY)

      const currentSid: string | null = yield select(selectCallSid)
      if (currentSid !== callSid) return
    }
  }
}

// Events of the call -> call state
function* handleCallEvent(event: TwilioCallEvent) {
  if (event.type === 'volume' || event.type === 'sample') {
    return
  }

  switch (event.type) {
    case 'ringing':
      yield put(setters.patchCall({ phase: 'ringing' }))
      break
    case 'accept': {
      yield put(
        setters.patchCall({ phase: 'connected', acceptedAt: Date.now() })
      )

      const callSid = event.call.parameters?.CallSid
      const storedSid: string | null = yield select(selectCallSid)
      if (callSid && storedSid !== callSid)
        yield call(resolveBackendCall, callSid)
      break
    }
    case 'mute':
      yield put(setters.patchCall({ muted: event.isMuted }))
      break
    case 'reconnecting':
      yield put(setters.patchCall({ phase: 'reconnecting' }))
      break
    case 'reconnected':
      yield put(setters.patchCall({ phase: 'connected' }))
      break
    case 'disconnect':
    case 'cancel':
    case 'reject':
    case 'error':
      yield put(setters.resetCall())
      break
  }
}

// Participant of an incoming call comes from the custom parameter of the call
const getIncomingParticipant = (call: Call): ICallParticipant => {
  const object = twilioService.getCallSubscriberObject(call)

  return {
    number: call.parameters?.From,
    name: object?.primary,
    secondary: object?.secondary,
    avatarUrl: object?.icon,
    scheme: object?.object_scheme_identifier,
    uniqId: object?.uniq_id,
  }
}

// Participant of an outgoing call: the storage object is either passed in or searched by number
function* getOutgoingParticipant(number: string, object?: TConnectObject) {
  let storageObject: TConnectObject | undefined = object

  if (!storageObject) {
    try {
      const response: IResponse<IListItem[]> = yield call(
        schemaService.getDataByObjectNumber,
        { searchValues: [number] }
      )
      // the list may be empty: there is no object for this number
      storageObject = response.data?.[0]
    } catch (err) {
      // the call must work even if the lookup failed
      console.error('Failed to get object by number', err)
    }
  }

  const participant: ICallParticipant = {
    number,
    name: storageObject?.primary,
    secondary: storageObject?.secondary,
    avatarUrl: storageObject?.icon,
    scheme: storageObject?.objectSchemeIdentifier,
    uniqId: storageObject?.uniqId,
  }

  return participant
}

function* watchCallChannel(channel: EventChannel<TwilioCallEvent>) {
  try {
    while (true) {
      const event: TwilioCallEvent = yield take(channel)

      yield call(handleCallEvent, event)

      if (CALL_FINAL_EVENTS.includes(event.type)) return
    }
  } finally {
    channel.close()
  }
}

function* watchTwilioChannel(channel: EventChannel<TwilioChannelEvent>) {
  try {
    while (true) {
      const event: TwilioChannelEvent = yield take(channel)

      switch (event.type) {
        case 'registering':
        case 'registered':
        case 'unregistered':
          yield put(setters.setDevice({ status: event.type }))
          break
        case 'error':
          yield put(
            setters.setDevice({
              status: 'error',
              error: (event.error as Error | undefined)?.message,
            })
          )
          break
      }

      if (event.type === 'incoming') {
        // callChanged (watcher start) has already come for this call; here is only incoming-specific data
        yield put(
          setters.setParticipants([getIncomingParticipant(event.call as Call)])
        )
        yield put(setters.patchCall({ phase: 'incoming' }))
      }

      if (event.type === 'callChanged') {
        // Covers both outgoing and incoming calls: the manager emits callChanged(call)
        // for each of them (before `incoming`), so the watcher is started only here.
        // callChanged(null) doesn't cancel anything: the call watcher finishes itself
        // after the final event, so that the final event isn't lost.
        // A new call replaces the previous one.
        if (event.call) {
          if (callWatcherTask) yield cancel(callWatcherTask)

          const callChannel: EventChannel<TwilioCallEvent> = yield call(
            createCallChannel,
            event.call as Call
          )
          callWatcherTask = yield fork(watchCallChannel, callChannel)
        }
      }

      if (event.type === 'tokenWillExpire') {
        const { token } = yield call(twilioService.getTwilioToken)

        yield call(
          [
            twilioService.twilioDeviceManager,
            twilioService.twilioDeviceManager.updateToken,
          ],
          token
        )
      }
    }
  } finally {
    channel.close()
  }
}

let watcherTask: Task | null = null
let callWatcherTask: Task | null = null

export default createSagas<Actions>(actions, {
  initialize: function* () {
    const { token } = yield call(twilioService.getTwilioToken)

    // Device is created but NOT registered yet
    yield call(
      [
        twilioService.twilioDeviceManager,
        twilioService.twilioDeviceManager.initialize,
      ],
      token
    )

    // Stop the watcher of the previous device (if any)
    if (watcherTask) yield cancel(watcherTask)

    // Listen to device events before registering so that none are missed
    const channel: EventChannel<TwilioChannelEvent> = yield call(
      createTwilioChannel
    )

    watcherTask = yield fork(watchTwilioChannel, channel)

    yield put(actions.register())
  },

  register: function* () {
    yield call([
      twilioService.twilioDeviceManager,
      twilioService.twilioDeviceManager.register,
    ])
  },

  connect: function* ({ payload }) {
    const { userId, toWhom, object } = payload

    if (!userId || !toWhom) return

    const participant: ICallParticipant = yield call(
      getOutgoingParticipant,
      toWhom,
      object
    )

    yield put(setters.setParticipants([participant]))
    yield put(setters.patchCall({ phase: 'dialing' }))

    try {
      yield call(
        [
          twilioService.twilioDeviceManager,
          twilioService.twilioDeviceManager.connect,
        ],
        { To: toWhom, CusmanAccountId: userId }
      )
    } catch (err) {
      yield put(setters.resetCall())
      throw err
    }
  },

  accept: function* () {
    // Twilio sets up media ("connecting") until the `accept` event comes
    yield put(setters.patchCall({ phase: 'dialing' }))
    yield call([
      twilioService.twilioDeviceManager,
      twilioService.twilioDeviceManager.accept,
    ])
  },

  // The call state is reset by the `reject` / `disconnect` events of the call
  reject: function* () {
    yield call([
      twilioService.twilioDeviceManager,
      twilioService.twilioDeviceManager.reject,
    ])
  },

  disconnect: function* () {
    yield call([
      twilioService.twilioDeviceManager,
      twilioService.twilioDeviceManager.disconnect,
    ])
  },

  // The `muted` flag is updated by the `mute` event of the call
  toggleMute: function* () {
    yield call([
      twilioService.twilioDeviceManager,
      twilioService.twilioDeviceManager.toggleMute,
    ])
  },

  sendDigits: function* ({ payload }) {
    yield call(
      [
        twilioService.twilioDeviceManager,
        twilioService.twilioDeviceManager.sendDigits,
      ],
      payload
    )
  },

  holdCurrentCall: function* () {
    const backendCall: IBackendCall | null = yield select(selectBackendCall)

    if (!backendCall?.id) return
    if (backendCall.mode !== 'hybrid' && backendCall.mode !== 'conference')
      return
    if (backendCall.escalationStatus === 'pending' || backendCall.onHold) return

    try {
      const updated: IBackendCall = yield call(
        twilioService.holdCall,
        backendCall.id
      )
      // The call may have ended while the request was in flight
      const current: IBackendCall | null = yield select(selectBackendCall)
      if (current?.id !== backendCall.id) return

      yield call(commitBackendCall, updated)
    } catch (err) {
      notify({
        type: 'error',
        title: 'Hold failed',
        description: getHoldErrorMessage(
          err,
          'Could not put the call on hold.'
        ),
      })
      throw err
    }
  },

  unholdCurrentCall: function* () {
    const backendCall: IBackendCall | null = yield select(selectBackendCall)

    if (!backendCall?.id) return
    if (backendCall.mode !== 'conference' || backendCall.onHold !== true) return

    try {
      const updated: IBackendCall = yield call(
        twilioService.unholdCall,
        backendCall.id
      )
      const current: IBackendCall | null = yield select(selectBackendCall)
      if (current?.id !== backendCall.id) return

      yield call(commitBackendCall, updated)
    } catch (err) {
      notify({
        type: 'error',
        title: 'Unhold failed',
        description: getHoldErrorMessage(
          err,
          'Could not take the call off hold.'
        ),
      })
      throw err
    }
  },

  addCallParticipant: function* ({ payload: userId }) {
    const backendCall: IBackendCall | null = yield select(selectBackendCall)

    if (!backendCall?.id) return
    if (backendCall.mode !== 'hybrid' && backendCall.mode !== 'conference')
      return

    const alreadyInCall = (backendCall.participants ?? []).some(
      (participant) =>
        participant.kind === 'user' &&
        participant.userId === userId &&
        ACTIVE_PARTICIPANT_STATUSES.has(participant.status)
    )
    if (alreadyInCall) return

    try {
      const updated: IBackendCall = yield call(
        twilioService.addCallParticipant,
        backendCall.id,
        userId
      )
      const current: IBackendCall | null = yield select(selectBackendCall)
      if (current?.id !== backendCall.id) return

      yield call(commitBackendCall, updated)
    } catch (err) {
      notify({
        type: 'error',
        title: 'Could not add participant',
        description: getApiErrorMessage(
          err,
          ADD_PARTICIPANT_ERROR_MESSAGES,
          'Could not add the participant.'
        ),
      })
      throw err
    }
  },

  transferCurrentCall: function* ({ payload }) {
    const { userId, mode } = payload as { userId: number; mode: TTransferMode }
    const backendCall: IBackendCall | null = yield select(selectBackendCall)

    if (!backendCall?.id || !canTransferCall(backendCall)) return
    if (backendCall.escalationStatus === 'pending') return
    if (ACTIVE_TRANSFER_STATUSES.has(backendCall.transfer?.status ?? '')) return

    const currentUserId: number | undefined = yield select(
      accountSelectors.userId
    )
    if (currentUserId != null && userId === currentUserId) return
    if (isUserAlreadyInCall(backendCall, userId)) return

    try {
      const updated: IBackendCall = yield call(
        twilioService.transferCall,
        backendCall.id,
        userId,
        mode
      )
      yield call(applyUpdatedCall, backendCall.id, updated)
    } catch (err) {
      notify({
        type: 'error',
        title: 'Transfer failed',
        description: getTransferErrorMessage(
          err,
          'Could not transfer the call.'
        ),
      })
      throw err
    }
  },

  completeCurrentTransfer: function* () {
    const backendCall: IBackendCall | null = yield select(selectBackendCall)

    if (!backendCall?.id) return
    if (backendCall.transfer?.status !== 'consulting') return

    try {
      const updated: IBackendCall = yield call(
        twilioService.completeTransfer,
        backendCall.id
      )
      yield call(applyUpdatedCall, backendCall.id, updated)
    } catch (err) {
      if (TRANSFER_STATE_MISMATCH.has(getApiErrorCode(err) ?? '')) {
        yield call(refreshBackendCall, backendCall.id)
      }
      notify({
        type: 'error',
        title: 'Could not complete the transfer',
        description: getTransferErrorMessage(
          err,
          'Could not complete the transfer.'
        ),
      })
      throw err
    }
  },

  cancelCurrentTransfer: function* () {
    const backendCall: IBackendCall | null = yield select(selectBackendCall)
    const status = backendCall?.transfer?.status

    if (!backendCall?.id) return
    if (status !== 'ringing' && status !== 'consulting') return

    try {
      const updated: IBackendCall = yield call(
        twilioService.cancelTransfer,
        backendCall.id
      )
      yield call(applyUpdatedCall, backendCall.id, updated)
    } catch (err) {
      if (getApiErrorCode(err) === 'no_transfer') {
        yield call(refreshBackendCall, backendCall.id)
      }
      notify({
        type: 'error',
        title: 'Could not cancel the transfer',
        description: getTransferErrorMessage(
          err,
          'Could not cancel the transfer.'
        ),
      })
      throw err
    }
  },

  getSubscriberCallHistory: {
    effect: 'latest',
    saga: function* ({ payload }) {
      const { subscriberStorageUniqId } = payload
      const saved: ReturnType<typeof selectSubscriberCallHistory> =
        yield select(selectSubscriberCallHistory)

      if (saved?.[subscriberStorageUniqId]) return

      yield put(
        setters.setSubscriberCallHistoryPending(subscriberStorageUniqId)
      )

      try {
        const callsHistory: IResponse<ICall[]> = yield call(
          twilioService.getCallHistory,
          { subscriberStorageUniqId }
        )

        yield put(
          setters.setSubscriberCallHistory({
            storageUniqId: subscriberStorageUniqId,
            data: callsHistory.data,
          })
        )
      } catch (err) {
        if (yield cancelled()) throw err

        yield put(
          setters.setSubscriberCallHistoryError(subscriberStorageUniqId)
        )
        throw err
      }
    },
  },
})
